import { View, useWindowDimensions } from 'react-native'
import React, { SetStateAction, useEffect, useState, Dispatch } from 'react'
import { appColors, appFonts, fontSize } from '../../themes/appTheme'
import ZenText from 'zen-core-elements/src/elements/zenText/zenText'
import { ZenButton, ZenDropdown, ZenIcon, ZenInput } from 'zen-core-elements'
import { useIsFocused } from '@react-navigation/native'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { ScrollView } from 'react-native'
import { OrderedProductListType } from './orderDetailModule'
import { useAppSelector } from '../../redux/hooks'

export type ReasonType = {
    "value": string,
    "code": string,
    "id": number,
    "additionalField": null,
    label: string
}

const ReturnReasonScreen = ({
    actionData,
    reasonData,
    setReturnRequestScreen,
    selectedProduct,
    updateRequest,
    width
}: {
    actionData: ReasonType[],
    reasonData: ReasonType[],
    setReturnRequestScreen: Dispatch<SetStateAction<boolean>>,
    selectedProduct: OrderedProductListType | null,
    isOrder: boolean,
    updateRequest: (args: { otherReasonValue: string, returnComment: string, reasonValue: string, actionValue: string, productQuantity: number, isOrder: boolean }) => void,
    width: number
}) => {
    const isFocus = useIsFocused()
    const { height } = useWindowDimensions()

    const [actionValue, setActionValue] = useState<ReasonType[] | []>([])
    const [reasonvalue, setReasonValue] = useState<ReasonType[] | []>([])
    const [openAction, setOpenAction] = useState(false)
    const [openReason, setOpenReason] = useState(false)
    const [selectedReason, setSelectedReason] = useState('')
    const [selectedAction, setSelectedAction] = useState(actionData?.find((i) => i.value === "Refund")?.value || "")
    const { returnRequestOrder, returnRequestproduct } = useAppSelector(state => state?.orderDetails)

    const validationSchema = yup.object().shape({
        isRequired: yup.boolean().default(false),
        productQuantity: yup
            .number()
            .when('isRequired', (isRequired, schema) =>
                Number(selectedProduct?.quantity)
                    ? schema.required('Product quantity is required')
                        .min(1, 'Quantity should be greater than 0.')
                        .max(Number(selectedProduct?.quantity), `Quantity should not exceed ordered count.${selectedProduct?.quantity}`)
                    : schema
            ),
        actionValue: yup.string().required('Action is required'),
        reasonValue: yup.string().required('Reason is required'),
        returnComment: yup.string(),
        otherReasonValue: yup.string()
            .when('reasonValue', (reasonValue, schema) =>
                reasonValue[0] == 'Others' ? schema.required('Other reason is required') : schema
            )
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            isRequired: selectedProduct != null ? true : false,
            productQuantity: 0,
            actionValue: actionData?.find((i) => i?.value == "Refund")?.value || '',
            reasonValue: '',
            returnComment: '',
            otherReasonValue: ''
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            let val = {
                actionValue: formik.values.actionValue,
                otherReasonValue: formik.values.otherReasonValue,
                productQuantity: formik.values.productQuantity ?? 0,
                reasonValue: formik.values.reasonValue,
                returnComment: formik.values.returnComment,
                isOrder: !formik.values.isRequired
            }
            updateRequest(val)
        }
    })

    useEffect(() => {
        if (isFocus) {
            formik.resetForm()
            setActionValue(DataFormatter(actionData) ?? [])
            setReasonValue(DataFormatter(reasonData) ?? [])
        }
    }, [isFocus])

    const DataFormatter = (value: ReasonType[]) => {
        if (value?.length) {
            let format = [...value]
            format = format.map((item) => {
                return { ...item, label: item.value }
            })
            return format
        } else {
            return null
        }
    }

    return (
        <View style={{ backgroundColor: appColors.light, width: '100%' }}>
            {/* Modal Header */}
            <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottomWidth: 1,
                borderBottomColor: appColors.coolGray,
                paddingBottom: 15,
                marginBottom: 20
            }}>
                <ZenText
                    content={'Return Order'}
                    bold
                    size={fontSize.headerFontSize}
                    color={appColors.primary}
                />
                <ZenIcon
                    name="close"
                    type="material-community"
                    size={24}
                    color={appColors.dark}
                    onPress={() => setReturnRequestScreen(false)}
                />
            </View>

            <ScrollView
                showsVerticalScrollIndicator={false}
                style={{ maxHeight: height * 0.7 }}
                nestedScrollEnabled={true}
                contentContainerStyle={{ paddingBottom: 20 }}
            >
                {/* Quantity Field */}
                {selectedProduct && (
                    <View style={{ marginBottom: 20, borderWidth: 1, borderColor: appColors.coolGray, padding: 15, borderRadius: 12 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                            <ZenIcon name="numeric" type="material-community" size={20} color={appColors.primary} style={{ marginRight: 8 }} />
                            <ZenText content="Return Quantity" bold size={fontSize.mediumFontSize} color={appColors.dark} />
                        </View>
                        <ZenInput
                            placeholder='Quantity'
                            inputMode='numeric'
                            containerStyle={{ height: 45, marginBottom: 5 }}
                            inputContainerStyle={{ borderRadius: 8, borderWidth: 1, borderColor: formik.touched.productQuantity && formik.errors.productQuantity ? appColors.red : appColors.coolGray, paddingHorizontal: 10 }}
                            onBlur={() => {
                                formik.setFieldTouched('productQuantity', true)
                            }}
                            onChangeText={(val) => {
                                formik.setFieldValue('productQuantity', val)
                            }}
                            value={formik.values.productQuantity ? String(formik.values.productQuantity) : ''}
                        />
                        {formik.touched.productQuantity && formik.errors.productQuantity && (
                            <ZenText content={formik.errors.productQuantity} size={fontSize.smallerFontSize} color={appColors.red} style={{ marginTop: 5, marginLeft: 5 }} />
                        )}
                    </View>
                )}

                {/* Reason Dropdown Field */}
                <View style={{ marginBottom: 20, borderWidth: 1, borderColor: appColors.coolGray, padding: 15, borderRadius: 12 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                        <ZenIcon name="help-circle-outline" type="material-community" size={20} color={appColors.primary} style={{ marginRight: 8 }} />
                        <ZenText content="Reason for Return" bold size={fontSize.mediumFontSize} color={appColors.dark} />
                    </View>
                    <ZenDropdown
                        placeholder='Select a Reason'
                        items={reasonvalue}
                        open={openReason}
                        setOpen={setOpenReason}
                        setValue={setSelectedReason}
                        value={selectedReason}
                        style={{ height: 45, borderColor: formik.touched.reasonValue && formik.errors.reasonValue ? appColors.red : appColors.coolGray, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10, backgroundColor: '#FFF' }}
                        containerStyle={{ width: '100%', marginBottom: 5 }}
                        dropDownContainerStyle={{ borderColor: appColors.coolGray, borderWidth: 1, borderRadius: 8, height: 150 }}
                        placeholderStyle={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: '#777' }}
                        onOpen={() => {
                            formik.setFieldTouched('reasonValue', true)
                        }}
                        onSelectItem={(value) => {
                            formik.setFieldValue('reasonValue', String(value.value))
                        }}
                    />
                    {formik.touched.reasonValue && formik.errors.reasonValue && (
                        <ZenText content={formik.errors.reasonValue} size={fontSize.smallerFontSize} color={appColors.red} style={{ marginTop: 5, marginLeft: 5 }} />
                    )}

                    {/* Other Reason Input */}
                    {formik.values.reasonValue === 'Others' && (
                        <View style={{ marginTop: 15 }}>
                            <ZenInput
                                placeholder='Enter other reason'
                                containerStyle={{ height: 45, marginBottom: 5 }}
                                inputContainerStyle={{ borderRadius: 8, borderWidth: 1, borderColor: formik.touched.otherReasonValue && formik.errors.otherReasonValue ? appColors.red : appColors.coolGray, paddingHorizontal: 10 }}
                                onBlur={() => {
                                    formik.setFieldTouched('otherReasonValue', true)
                                }}
                                onChangeText={(val) => {
                                    formik.setFieldValue('otherReasonValue', val)
                                }}
                                value={formik.values.otherReasonValue}
                            />
                            {formik.touched.otherReasonValue && formik.errors.otherReasonValue && (
                                <ZenText content={formik.errors.otherReasonValue} size={fontSize.smallerFontSize} color={appColors.red} style={{ marginTop: 5, marginLeft: 5 }} />
                            )}
                        </View>
                    )}
                </View>

                {/* Action Dropdown Field */}
                <View style={{ marginBottom: 20, borderWidth: 1, borderColor: appColors.coolGray, padding: 15, borderRadius: 12 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                        <ZenIcon name="swap-horizontal" type="material-community" size={20} color={appColors.primary} style={{ marginRight: 8 }} />
                        <ZenText content="Return Action" bold size={fontSize.mediumFontSize} color={appColors.dark} />
                    </View>
                    <ZenDropdown
                        placeholder='Select an Action'
                        items={actionValue}
                        open={openAction}
                        setOpen={setOpenAction}
                        setValue={setSelectedAction}
                        value={selectedAction}
                        disabled={true}
                        style={{ height: 45, borderColor: formik.touched.actionValue && formik.errors.actionValue ? appColors.red : appColors.coolGray, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10, backgroundColor: '#FFF' }}
                        containerStyle={{ width: '100%', marginBottom: 5 }}
                        dropDownContainerStyle={{ borderColor: appColors.coolGray, borderWidth: 1, borderRadius: 8, height: 100 }}
                        placeholderStyle={{ fontFamily: appFonts.medium, fontSize: fontSize.mediumFontSize, color: '#777' }}
                        onOpen={() => {
                            formik.setFieldTouched('actionValue', true)
                        }}
                        onSelectItem={(value) => {
                            formik.setFieldValue('actionValue', String(value.value))
                        }}
                    />
                    {formik.touched.actionValue && formik.errors.actionValue && (
                        <ZenText content={formik.errors.actionValue} size={fontSize.smallerFontSize} color={appColors.red} style={{ marginTop: 5, marginLeft: 5 }} />
                    )}
                </View>

                {/* Comment Field */}
                <View style={{ marginBottom: 20, borderWidth: 1, borderColor: appColors.coolGray, padding: 15, borderRadius: 12 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                        <ZenIcon name="comment-text-outline" type="material-community" size={20} color={appColors.primary} style={{ marginRight: 8 }} />
                        <ZenText content="Additional Comments" bold size={fontSize.mediumFontSize} color={appColors.dark} />
                    </View>
                    <ZenInput
                        placeholder='Enter comments...'
                        multiline
                        containerStyle={{ height: 80, marginBottom: 5 }}
                        inputContainerStyle={{ borderRadius: 8, borderWidth: 1, borderColor: appColors.coolGray, paddingHorizontal: 10, height: 80, alignItems: 'flex-start' }}
                        inputStyle={{ textAlignVertical: 'top' }}
                        onBlur={() => {
                            formik.setFieldTouched('returnComment', true)
                        }}
                        onChangeText={(val) => {
                            formik.setFieldValue('returnComment', val)
                        }}
                        value={formik.values.returnComment}
                    />
                </View>

                {/* Save Button */}
                <ZenButton
                    title="Save"
                    loading={returnRequestOrder?.loading || returnRequestproduct?.loading}
                    onPress={() => formik.handleSubmit()}
                    buttonStyle={{ backgroundColor: appColors.primary, borderRadius: 8, height: 45 }}
                    titleStyle={{ fontSize: fontSize.mediumFontSize }}
                    containerStyle={{ marginTop: 10 }}
                />

                {/* Cancel Button */}
                <ZenButton
                    title="Cancel"
                    disabled={returnRequestOrder?.loading || returnRequestproduct?.loading}
                    type="clear"
                    onPress={() => setReturnRequestScreen(false)}
                    titleStyle={{ color: appColors.lightDark, fontSize: fontSize.mediumFontSize }}
                    containerStyle={{ marginTop: 10 }}
                />
            </ScrollView>
        </View>
    )
}

export default ReturnReasonScreen


